/**
 * decision-guides.ts — queue #667 §UAT-2026-07-09-R667 §R667-2.
 *
 * 목적이 겹치는 능력군(이벤트 전파·캐시·페이징·파일 백엔드·인증·조회) 6종을 손수
 * 큐레이션한 정적 데이터 + 순수 파생 함수. 카탈로그는 브라우징(패싯 §R640)·가져다쓰기
 * (Take-this §R630/§R638·전이 §R639)·조합(레시피 §R664)·설정(§R665)·트러블슈팅(함정
 * §R666)까지 완비됐으나, 신규 프로젝트의 첫 기술 선택 지점을 돕는 비교 가이드가 없었다 —
 * 본 파일은 '언제 무엇을 쓰나'를 한자리에서 비교하는 데이터를 신규 데이터 원천 0 으로
 * 채운다(전체 실존 모듈 코드만 엮은 정적 정의).
 *
 * ★재사용·순수파생 원칙(§R664 recipes.ts · §R666 pitfalls.ts 계보 답습) — 선택 가이드는
 * 손수 판단·큐레이션한 지식(설계 지식·운영 비용/인프라 판단은 기계 추출 불가)이라 빌드타임
 * 추출/git 파싱 대상이 아니다(신규 추출 스크립트 0). 사람이 읽는 텍스트(title/summary/
 * label/scenario/opsCost/infra/prerequisite)는 전체 i18n 키(ko/en 대칭 · §R656 로케일 부채
 * 재발 방지). 성숙도(GA/BETA/DEPRECATED)는 데이터에 저장하지 않고 §R643 `deriveMaturity`
 * 로 렌더 시점 파생한다(단일 진실 원천 유지 · 중복 저장 0).
 * ★시한폭탄 없음 — 선택 데이터·파생은 일자 의존 0(정적 지식). `now` 주입 규약 해당 없음.
 * ★재구현 금지 — 선택 데이터는 비교 판단의 정적 표현일 뿐 실행 코드 0줄. 권장은 공통
 * 능력 사용을 안내할 뿐 자체 우회 구현을 유도하지 않는다.
 *
 * ★file 군 — `C009`(FileManager SPI)는 local/S3 두 백엔드가 **동일 모듈의 backend 프로퍼티
 * 전환**(S3Adapter C059 는 구현 상세사항)이라 별도 후보로 쪼개지 않고 1개 옵션으로 병합했다
 * (§R667-1 실측 대조 표가 이 군을 C009·C060 2코드로만 집계한 것과 정합 · 6군 옵션 합계
 * 14 = §R667-10 "14 후보 코드"와 일치).
 */

/** 비교군 내 후보 1개 — 실존 코드 + 비교 축(i18n 키) + 권장 여부 */
export interface DecisionOption {
  code: string; // 실존 모듈 코드(take-this.json 키) — 예 'C008'
  labelKey: string; // showcase.decision.<guideId>.option.<code>.label (짧은 후보 라벨)
  scenarioKey: string; // showcase.decision.<guideId>.option.<code>.scenario (적합 시나리오 · 언제 쓰나)
  opsCostKey: string; // .opsCost (운영 비용 · 낮음/중간/높음 + 설명)
  infraKey: string; // .infra (인프라 요구 · 없음/Redis/Kafka broker 등)
  prerequisiteKey: string; // .prerequisite (전제 조건)
  recommended?: boolean; // 군당 정확히 1개 true — 권장 기본값(첫 선택)
  // 성숙도(GA/BETA/DEPRECATED)는 데이터에 저장하지 않고 렌더 시점 maturity.ts deriveMaturity(code 노드) 파생.
}

/** 목적이 겹치는 능력 비교군 1개 */
export interface DecisionGuide {
  id: string; // kebab-case 안정 id(딥링크 键) — 예 'messaging'
  titleKey: string; // showcase.decision.<id>.title (군 제목)
  summaryKey: string; // showcase.decision.<id>.summary (1줄 목적 · 이 군은 무엇을 고르나)
  options: DecisionOption[]; // 비교 후보(실존 코드 · 권장 1개)
  recipeIds?: string[]; // (선택) 관련 레시피 id(recipes.ts 실존) — 딥링크
  pitfallIds?: string[]; // (선택) 관련 함정 id(pitfalls.ts 실존) — 딥링크
}

/**
 * 군의 권장 기본값 후보(recommended:true) 반환 — 없거나 2개 이상이면 호출부가 실패한다
 * (단위테스트가 이 예외를 회귀 고정 — §R667-2).
 */
export function recommendedOption(guide: DecisionGuide): DecisionOption {
  const recs = guide.options.filter((o) => o.recommended);
  if (recs.length !== 1) throw new Error(`guide ${guide.id} must have exactly 1 recommended option`);
  return recs[0];
}

/** 특정 모듈 코드가 후보로 포함된 군만(모듈 상세 '대안과 비교' 진입점 데이터). */
export function decisionGuideForCode(code: string, guides: DecisionGuide[]): DecisionGuide[] {
  return guides.filter((g) => g.options.some((o) => o.code === code));
}

/** 군의 전체 후보 코드 목록(검색 subtitle·단위테스트 코드 실재 대조용). */
export function guideOptionCodes(guide: DecisionGuide): string[] {
  return guide.options.map((o) => o.code);
}

// ─── 6개 비교군 (§R667-2 시드 표 그대로 · ★ = 권장 기본값) ────────────────────────

export const DECISION_GUIDES: DecisionGuide[] = [
  {
    id: 'messaging',
    titleKey: 'showcase.decision.messaging.title',
    summaryKey: 'showcase.decision.messaging.summary',
    recipeIds: ['event-integration', 'batch-notify'],
    pitfallIds: ['optin-broker-health-down', 'sse-logging-filter-commit'],
    options: [
      {
        code: 'C008',
        labelKey: 'showcase.decision.messaging.option.C008.label',
        scenarioKey: 'showcase.decision.messaging.option.C008.scenario',
        opsCostKey: 'showcase.decision.messaging.option.C008.opsCost',
        infraKey: 'showcase.decision.messaging.option.C008.infra',
        prerequisiteKey: 'showcase.decision.messaging.option.C008.prerequisite',
        recommended: true,
      },
      {
        code: 'C024',
        labelKey: 'showcase.decision.messaging.option.C024.label',
        scenarioKey: 'showcase.decision.messaging.option.C024.scenario',
        opsCostKey: 'showcase.decision.messaging.option.C024.opsCost',
        infraKey: 'showcase.decision.messaging.option.C024.infra',
        prerequisiteKey: 'showcase.decision.messaging.option.C024.prerequisite',
      },
      {
        code: 'C056',
        labelKey: 'showcase.decision.messaging.option.C056.label',
        scenarioKey: 'showcase.decision.messaging.option.C056.scenario',
        opsCostKey: 'showcase.decision.messaging.option.C056.opsCost',
        infraKey: 'showcase.decision.messaging.option.C056.infra',
        prerequisiteKey: 'showcase.decision.messaging.option.C056.prerequisite',
      },
      {
        code: 'C057',
        labelKey: 'showcase.decision.messaging.option.C057.label',
        scenarioKey: 'showcase.decision.messaging.option.C057.scenario',
        opsCostKey: 'showcase.decision.messaging.option.C057.opsCost',
        infraKey: 'showcase.decision.messaging.option.C057.infra',
        prerequisiteKey: 'showcase.decision.messaging.option.C057.prerequisite',
      },
    ],
  },
  {
    id: 'cache',
    titleKey: 'showcase.decision.cache.title',
    summaryKey: 'showcase.decision.cache.summary',
    recipeIds: ['cache-guard'],
    pitfallIds: ['redis-repo-misclaim'],
    options: [
      {
        code: 'C051',
        labelKey: 'showcase.decision.cache.option.C051.label',
        scenarioKey: 'showcase.decision.cache.option.C051.scenario',
        opsCostKey: 'showcase.decision.cache.option.C051.opsCost',
        infraKey: 'showcase.decision.cache.option.C051.infra',
        prerequisiteKey: 'showcase.decision.cache.option.C051.prerequisite',
        recommended: true,
      },
      {
        code: 'C050',
        labelKey: 'showcase.decision.cache.option.C050.label',
        scenarioKey: 'showcase.decision.cache.option.C050.scenario',
        opsCostKey: 'showcase.decision.cache.option.C050.opsCost',
        infraKey: 'showcase.decision.cache.option.C050.infra',
        prerequisiteKey: 'showcase.decision.cache.option.C050.prerequisite',
      },
    ],
  },
  {
    id: 'paging',
    titleKey: 'showcase.decision.paging.title',
    summaryKey: 'showcase.decision.paging.summary',
    pitfallIds: ['json-nonnull-undefined'],
    options: [
      {
        code: 'C042',
        labelKey: 'showcase.decision.paging.option.C042.label',
        scenarioKey: 'showcase.decision.paging.option.C042.scenario',
        opsCostKey: 'showcase.decision.paging.option.C042.opsCost',
        infraKey: 'showcase.decision.paging.option.C042.infra',
        prerequisiteKey: 'showcase.decision.paging.option.C042.prerequisite',
        recommended: true,
      },
      {
        code: 'C048',
        labelKey: 'showcase.decision.paging.option.C048.label',
        scenarioKey: 'showcase.decision.paging.option.C048.scenario',
        opsCostKey: 'showcase.decision.paging.option.C048.opsCost',
        infraKey: 'showcase.decision.paging.option.C048.infra',
        prerequisiteKey: 'showcase.decision.paging.option.C048.prerequisite',
      },
    ],
  },
  {
    id: 'file',
    titleKey: 'showcase.decision.file.title',
    summaryKey: 'showcase.decision.file.summary',
    recipeIds: ['file-pipeline'],
    pitfallIds: ['oom-zip-inmemory'],
    options: [
      {
        code: 'C009',
        labelKey: 'showcase.decision.file.option.C009.label',
        scenarioKey: 'showcase.decision.file.option.C009.scenario',
        opsCostKey: 'showcase.decision.file.option.C009.opsCost',
        infraKey: 'showcase.decision.file.option.C009.infra',
        prerequisiteKey: 'showcase.decision.file.option.C009.prerequisite',
        recommended: true,
      },
      {
        code: 'C060',
        labelKey: 'showcase.decision.file.option.C060.label',
        scenarioKey: 'showcase.decision.file.option.C060.scenario',
        opsCostKey: 'showcase.decision.file.option.C060.opsCost',
        infraKey: 'showcase.decision.file.option.C060.infra',
        prerequisiteKey: 'showcase.decision.file.option.C060.prerequisite',
      },
    ],
  },
  {
    id: 'auth',
    titleKey: 'showcase.decision.auth.title',
    summaryKey: 'showcase.decision.auth.summary',
    recipeIds: ['auth-gating'],
    pitfallIds: ['permitall-antpath-npe', 'filter-chain-granular-403'],
    options: [
      {
        code: 'C028',
        labelKey: 'showcase.decision.auth.option.C028.label',
        scenarioKey: 'showcase.decision.auth.option.C028.scenario',
        opsCostKey: 'showcase.decision.auth.option.C028.opsCost',
        infraKey: 'showcase.decision.auth.option.C028.infra',
        prerequisiteKey: 'showcase.decision.auth.option.C028.prerequisite',
        recommended: true,
      },
      {
        code: 'C035',
        labelKey: 'showcase.decision.auth.option.C035.label',
        scenarioKey: 'showcase.decision.auth.option.C035.scenario',
        opsCostKey: 'showcase.decision.auth.option.C035.opsCost',
        infraKey: 'showcase.decision.auth.option.C035.infra',
        prerequisiteKey: 'showcase.decision.auth.option.C035.prerequisite',
      },
    ],
  },
  {
    id: 'query',
    titleKey: 'showcase.decision.query.title',
    summaryKey: 'showcase.decision.query.summary',
    pitfallIds: ['optional-dep-ncdfe'],
    options: [
      {
        code: 'C041',
        labelKey: 'showcase.decision.query.option.C041.label',
        scenarioKey: 'showcase.decision.query.option.C041.scenario',
        opsCostKey: 'showcase.decision.query.option.C041.opsCost',
        infraKey: 'showcase.decision.query.option.C041.infra',
        prerequisiteKey: 'showcase.decision.query.option.C041.prerequisite',
        recommended: true,
      },
      {
        code: 'C053',
        labelKey: 'showcase.decision.query.option.C053.label',
        scenarioKey: 'showcase.decision.query.option.C053.scenario',
        opsCostKey: 'showcase.decision.query.option.C053.opsCost',
        infraKey: 'showcase.decision.query.option.C053.infra',
        prerequisiteKey: 'showcase.decision.query.option.C053.prerequisite',
      },
    ],
  },
];
